import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import ImageUpload, { resolveImageUrl } from "./ImageUpload";
import { Store, Plus, Trash2, Save, Loader2, X, Pencil, PackageCheck } from "lucide-react";

const emptyProduct = { name: "", description: "", price: 0, images: [], category: "", active: true, featured: false, sortOrder: 0, variants: [] };

const ShopProductsAdmin = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/shop/products"); setProducts(data || []); }
    catch { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const p = editing;
    if (!p.name.trim()) { toast.error("Le nom est obligatoire"); return; }
    if (Number(p.price) <= 0) { toast.error("Le prix doit être supérieur à 0"); return; }
    if (!p.variants.length) { toast.error("Ajoutez au moins une déclinaison (taille/couleur + stock)"); return; }
    setSaving(true);
    const body = { ...p, price: Number(p.price), sortOrder: Number(p.sortOrder) || 0, variants: p.variants.map((v) => ({ ...v, stock: Number(v.stock) || 0 })) };
    try {
      if (p.id) await api.put(`/admin/shop/products/${p.id}`, body);
      else await api.post("/admin/shop/products", body);
      toast.success("Produit enregistré");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer « ${p.name} » ?`)) return;
    try { await api.delete(`/admin/shop/products/${p.id}`); toast.success("Produit supprimé"); load(); }
    catch { toast.error("Erreur"); }
  };

  const setField = (k, v) => setEditing((e) => ({ ...e, [k]: v }));
  const setVariant = (i, k, v) => setEditing((e) => ({ ...e, variants: e.variants.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const addVariant = () => setEditing((e) => ({ ...e, variants: [...e.variants, { size: "", color: "", stock: 0 }] }));
  const delVariant = (i) => setEditing((e) => ({ ...e, variants: e.variants.filter((_, j) => j !== i) }));

  return (
    <div data-testid="shop-products-admin">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="font-serif-display text-2xl sm:text-3xl text-stone-800 flex items-center gap-2"><Store size={24} /> Produits boutique</h1>
          <p className="text-stone-500 mt-1 text-sm sm:text-base">Goodies vendus en ligne : photo, prix, tailles/couleurs et stock par déclinaison.</p>
        </div>
        <button onClick={() => setEditing({ ...emptyProduct })} className="inline-flex items-center gap-2 bg-coral text-white px-5 py-2.5 rounded-full text-sm shrink-0" data-testid="shop-product-new">
          <Plus size={15} /> Nouveau produit
        </button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-stone-400"><Loader2 className="animate-spin inline" size={20} /></div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((p) => {
            const stock = (p.variants || []).reduce((s, v) => s + (Number(v.stock) || 0), 0);
            return (
              <div key={p.id} className="bg-white rounded-2xl overflow-hidden shadow-sm" data-testid={`shop-product-row-${p.id}`}>
                <div className="aspect-[4/3] bg-stone-100">
                  {(p.images || [])[0] ? <img src={resolveImageUrl(p.images[0])} alt={p.name} className="w-full h-full object-cover" /> : null}
                </div>
                <div className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h2 className="font-serif-display text-base md:text-lg text-stone-800 truncate">{p.name}</h2>
                      <p className="text-xs text-stone-500">{p.category || "—"} · {Number(p.price).toFixed(2)} €</p>
                    </div>
                    {!p.active && <span className="text-[10px] uppercase bg-stone-100 text-stone-500 px-2 py-0.5 rounded-full shrink-0">Masqué</span>}
                  </div>
                  <p className={`text-xs mt-2 ${stock > 0 ? "text-emerald-700" : "text-rose-600"}`}>
                    <PackageCheck size={12} className="inline mr-1" />{stock} en stock ({(p.variants || []).length} décl.)
                  </p>
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => setEditing({ ...emptyProduct, ...p })} className="flex-1 inline-flex items-center justify-center gap-1.5 bg-stone-100 hover:bg-stone-200 text-stone-700 px-3 py-2 rounded-lg text-xs" data-testid={`shop-product-edit-${p.id}`}><Pencil size={13} /> Modifier</button>
                    <button onClick={() => remove(p)} className="p-2 rounded-lg text-rose-600 hover:bg-rose-50" data-testid={`shop-product-delete-${p.id}`}><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            );
          })}
          {products.length === 0 && <p className="text-stone-400 italic text-sm">Aucun produit pour l&apos;instant.</p>}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-stone-900/60 flex items-start justify-center p-3 overflow-y-auto" onClick={() => setEditing(null)} data-testid="shop-product-modal">
          <div className="bg-white rounded-2xl w-full max-w-2xl my-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
              <h3 className="font-serif-display text-stone-800">{editing.id ? "Modifier le produit" : "Nouveau produit"}</h3>
              <button onClick={() => setEditing(null)} className="p-1.5 rounded-lg hover:bg-stone-100" data-testid="shop-product-modal-close"><X size={17} /></button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">Nom *</label>
                  <input value={editing.name} onChange={(e) => setField("name", e.target.value)} data-testid="shop-product-name" className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">Prix (€) *</label>
                  <input type="number" step="0.5" value={editing.price} onChange={(e) => setField("price", e.target.value)} data-testid="shop-product-price" className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">Catégorie</label>
                  <input value={editing.category} onChange={(e) => setField("category", e.target.value)} placeholder="Textile, Accessoires…" data-testid="shop-product-category" className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">Ordre d&apos;affichage</label>
                  <input type="number" value={editing.sortOrder} onChange={(e) => setField("sortOrder", e.target.value)} data-testid="shop-product-order" className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2" />
                </div>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">Description</label>
                <textarea value={editing.description} onChange={(e) => setField("description", e.target.value)} rows={3} data-testid="shop-product-description" className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-sm" />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">Photos</label>
                <div className="flex gap-2 flex-wrap items-start">
                  {(editing.images || []).map((img, i) => (
                    <div key={img} className="relative">
                      <img src={resolveImageUrl(img)} alt="" className="h-20 w-20 object-cover rounded-lg" />
                      <button onClick={() => setField("images", editing.images.filter((_, j) => j !== i))} className="absolute -top-1.5 -right-1.5 bg-white border border-stone-200 rounded-full p-1" data-testid={`shop-product-image-remove-${i}`}><X size={11} /></button>
                    </div>
                  ))}
                  <ImageUpload value="" onChange={(url) => url && setField("images", [...(editing.images || []), url])} />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs uppercase tracking-widest text-stone-500">Déclinaisons & stock *</label>
                  <button onClick={addVariant} className="inline-flex items-center gap-1 text-xs text-deep-rose" data-testid="shop-variant-add"><Plus size={13} /> Ajouter</button>
                </div>
                <div className="space-y-2">
                  {(editing.variants || []).map((v, i) => (
                    <div key={i} className="grid grid-cols-[1fr_1fr_90px_36px] gap-2 items-center" data-testid={`shop-variant-${i}`}>
                      <input value={v.size || ""} onChange={(e) => setVariant(i, "size", e.target.value)} placeholder="Taille (S, M…)" data-testid={`shop-variant-size-${i}`} className="bg-stone-50 border border-stone-200 rounded-lg px-2.5 py-2 text-sm" />
                      <input value={v.color || ""} onChange={(e) => setVariant(i, "color", e.target.value)} placeholder="Couleur" data-testid={`shop-variant-color-${i}`} className="bg-stone-50 border border-stone-200 rounded-lg px-2.5 py-2 text-sm" />
                      <input type="number" value={v.stock} onChange={(e) => setVariant(i, "stock", e.target.value)} placeholder="Stock" data-testid={`shop-variant-stock-${i}`} className="bg-stone-50 border border-stone-200 rounded-lg px-2.5 py-2 text-sm" />
                      <button onClick={() => delVariant(i)} className="p-2 rounded-lg text-rose-600 hover:bg-rose-50" data-testid={`shop-variant-delete-${i}`}><Trash2 size={14} /></button>
                    </div>
                  ))}
                  {(editing.variants || []).length === 0 && <p className="text-xs text-stone-400 italic">Une déclinaison minimum. Laissez taille et couleur vides pour un produit unique.</p>}
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-stone-700">
                <input type="checkbox" checked={!!editing.active} onChange={(e) => setField("active", e.target.checked)} data-testid="shop-product-active" className="rounded" />
                Visible dans la boutique
              </label>

              <button onClick={save} disabled={saving} className="w-full inline-flex items-center justify-center gap-2 bg-coral text-white px-5 py-3 rounded-full text-sm disabled:opacity-50" data-testid="shop-product-save">
                {saving ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopProductsAdmin;
